/**
 * R262 v2: persist the last-known `isRunning` flag in localStorage so the
 * SPA can render the active-trade-card placeholder IMMEDIATELY on F5
 * reload — without the brief CARREGANDO → NeuralCore → LIGAR ROBO flicker
 * that happens while `useBotSessionState` is still loading.
 *
 * Stale window: 10 minutes. A trade lasts ~60 s and the natural cycle
 * gap is ~21 s, so the flag is "fresh enough" for at most ~10 min of
 * idle browser tab. After that we fall back to the normal loading UX
 * (better to show LIGAR ROBO than a stale active-trade ghost).
 */

const STORAGE_KEY = "quartel:bot-was-running";
const FRESH_WINDOW_MS = 10 * 60_000;

type PersistedFlag = {
  readonly isRunning: boolean;
  readonly observedAtMs: number;
};

/** Read the persisted flag from localStorage. Returns null if missing,
 *  malformed, or older than `FRESH_WINDOW_MS`. */
export function readPersistedSessionRunning(
  now: number = Date.now(),
): boolean | null {
  if (typeof globalThis.localStorage === "undefined") return null;
  let raw: string | null;
  try {
    raw = globalThis.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  const parsed = parsePersistedFlag(raw);
  if (parsed === null) return null;
  if (now - parsed.observedAtMs > FRESH_WINDOW_MS) return null;
  return parsed.isRunning;
}

/** Write the flag. Safe in environments without localStorage. */
export function writePersistedSessionRunning(
  isRunning: boolean,
  now: number = Date.now(),
): void {
  if (typeof globalThis.localStorage === "undefined") return;
  const payload: PersistedFlag = { isRunning, observedAtMs: now };
  try {
    globalThis.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // localStorage may be disabled (Safari private mode, quota exceeded);
    // swallowing keeps the SPA functional, just without the optimistic
    // active-trade placeholder on reload.
  }
}

/** Clear the persisted flag. Called from `useStopBotSession.onSuccess` so an
 *  explicit DESLIGAR removes the optimistic active-trade placeholder on the
 *  very next F5 — without it, the placeholder would linger for ~10 minutes
 *  even though the user already stopped the bot. */
export function clearPersistedSessionRunning(): void {
  if (typeof globalThis.localStorage === "undefined") return;
  try {
    globalThis.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}

/** Test-only alias retained for back-compat with R262 spec. */
export function clearPersistedSessionRunningForTests(): void {
  clearPersistedSessionRunning();
}

function parsePersistedFlag(raw: string): PersistedFlag | null {
  try {
    const data = JSON.parse(raw) as unknown;
    if (typeof data !== "object" || data === null) return null;
    const record = data as Record<string, unknown>;
    if (typeof record["isRunning"] !== "boolean") return null;
    if (typeof record["observedAtMs"] !== "number") return null;
    return {
      isRunning: record["isRunning"],
      observedAtMs: record["observedAtMs"],
    };
  } catch {
    return null;
  }
}
